import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';

import type {FormPreviewInput} from '@/components/FormPreview';
import FormPreview from '@/components/FormPreview';
import {Typography} from '@/components/ui/typography';
import {graphqlClient} from '@/services/graphqlClient';
import type {Resident, Task, User} from '@/services/graphqlHooks';
import {
  useGetAllCurrentResidentsForInputQuery,
  useGetAllUsersForInputQuery,
} from '@/services/graphqlHooks';
import ResidentAvatar from '@/shared/residents/components/ResidentAvatar';
import UserAvatar from '@/shared/users/components/UserAvatar';
import partOfTheDay from '@/translation/partOfTheDay';
import {TIME_FORMATS} from '@/utils/timeFormats';

export type FormPreviewProps = {
  task?: Task;
};

const TaskFormPreview = ({task}: FormPreviewProps) => {
  const {t} = useTranslation();
  const {data: userData} = useGetAllUsersForInputQuery(
    graphqlClient,
    undefined,
    {staleTime: Infinity}
  );
  const {data: residentData} = useGetAllCurrentResidentsForInputQuery(
    graphqlClient,
    undefined,
    {staleTime: Infinity}
  );
  const users = (userData?.getAllUsers || []) as User[];
  const residents = (residentData?.getAllCurrentResidents || []) as Resident[];

  const assignedToResident = residents?.find(
    resident => resident.id === task?.assignedToResident
  );

  const assignedToEmployee = users?.find(
    user => user.id === task?.assignedToEmployee
  );

  const completedBy = users?.find(user => user.userName === task?.completedBy);

  const formPreviewItems: FormPreviewInput[] = [
    {
      label: t('Resident'),
      value: assignedToResident ? (
        <ResidentAvatar resident={assignedToResident} />
      ) : (
        ''
      ),
      key: 'assignedToResident',
    },
    {
      label: t('Employee'),
      value: assignedToEmployee ? <UserAvatar user={assignedToEmployee} /> : '',
      key: 'assignedToEmployee',
    },
    {
      label: t('Date'),
      value: task?.date
        ? task?.completeUntil?.time
          ? dayjs(`${task.date}T${task?.completeUntil?.time}`).format(
              TIME_FORMATS.awsDate
            )
          : dayjs(task.date).format(TIME_FORMATS.awsDate)
        : '',
      key: 'date',
    },
    {
      label: t('Time'),
      value: task?.completeUntil?.time
        ? dayjs(`${task.date}T${task?.completeUntil?.time}`).format('HH:mm')
        : '',
      key: 'time',
    },
    {
      label: t('Part of the day'),
      value: task?.completeUntil?.partOfTheDay
        ? partOfTheDay[task?.completeUntil?.partOfTheDay]
        : '',
      key: 'partOfTheDay',
    },
    {
      label: t('Completed at'),
      value: task?.completedAt
        ? dayjs(task.completedAt).format(TIME_FORMATS.dateTime)
        : '',
      key: 'completedAt',
    },
    {
      label: t('Completed by'),
      value: completedBy ? <UserAvatar user={completedBy} /> : '',
      key: 'completedBy',
    },
    {
      label: t('Comment'),
      value: task?.comment ? <Typography>{task?.comment}</Typography> : '',
      key: 'comment',
    },
    {
      label: t('Rejection note'),
      value: task?.rejectionNote ? (
        <Typography className="text-red-600">{task?.rejectionNote}</Typography>
      ) : (
        ''
      ),
      key: 'rejectionNote',
    },
  ];

  return (
    <div>
      <div className="mb-5 flex flex-col gap-2">
        <Typography
          asChild
          className="text-lg font-semibold placeholder-gray-800"
        >
          <h1>{task?.title}</h1>
        </Typography>
        {task?.content && (
          <Typography asChild>
            <h2>{task.content}</h2>
          </Typography>
        )}
      </div>
      <FormPreview items={formPreviewItems} hideEmpty />
    </div>
  );
};

export default TaskFormPreview;
